import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/warning-modal/warning-modal.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import {
  COMMON_SCREENING_METHOD_OPTION,
  CONST,
  SCREENING_METHOD_OPTION,
  SCREENING_TYPE_OPTION,
  SORT_CODE_OPTION,
} from './new-security-screening-modal.constant';

@Component({
  selector: 'app-new-security-screening-modal',
  templateUrl: './new-security-screening-modal.component.html',
  styleUrls: ['./new-security-screening-modal.component.scss'],
  standalone: false,
})
export class NewSecurityScreeningModalComponent implements OnInit {
  private readonly destroy = inject(DestroyRef);

  readonly CONST = {
    ...CONST,
  };
  readonly IMAGES = IMAGE_SRC;
  readonly SCREENING_TYPE_OPTION = SCREENING_TYPE_OPTION;
  readonly SCREENING_METHOD_OPTION = SCREENING_METHOD_OPTION;
  readonly COMMON_SCREENING_METHOD_OPTION = COMMON_SCREENING_METHOD_OPTION;
  readonly SORT_CODE_OPTION = SORT_CODE_OPTION;
  readonly SpecialCharacters = SpecialCharacters;

  currentDate: Date = new Date();
  minDate: Date = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    this.currentDate.getDate() - 1,
  );
  maxDate: Date = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    this.currentDate.getDate() + 3,
  );
  newSecurityScreeninForm!: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FormGroup['value'],
    private readonly dialogRef: MatDialogRef<NewSecurityScreeningModalComponent>,
    private fb: FormBuilder,
    public readonly commonUtils: CommonUtils,
    private dialog: MatDialog,
    private dataUtils: DataUtils,
  ) {}

  /**
   * OnInit Lifecycle hook
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  ngOnInit(): void {
    this.initForm();
    this.formValueChanges();
    this.setFormValue();
  }

  /**
   * Init form with form controls
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  initForm() {
    this.newSecurityScreeninForm = this.fb.group({
      screeningType: [null, [Validators.required]],
      screeningMethod: [null, [Validators.required]],
      screenerID: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(36)]),
      ],
      trackingNumber: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(18)]),
      ],
      secondaryScreening: [false],
      secondaryScreeningMethod: [null, [Validators.required]],
      sortDate: [new Date(), [Validators.required]],
      sortCode: [null, [Validators.required]],
    });
    this.newSecurityScreeninForm.get('secondaryScreeningMethod')?.disable();
  }

  /**
   * get form value changes
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  formValueChanges() {
    this.newSecurityScreeninForm
      .get('screeningType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((screeningType) => {
        const isScreenTypeTwo = screeningType === 'SEC SCR 1';

        if (isScreenTypeTwo) {
          this.getFormControl('secondaryScreening')?.enable();
        } else {
          this.getFormControl('secondaryScreening')?.reset(false);
          this.getFormControl('secondaryScreening')?.disable();
        }
        const isCommonVerif = screeningType === 'COMM VERIF';
        if (isCommonVerif) {
          this.getFormControl('screeningMethod')?.patchValue(null);
        }
      });
    this.newSecurityScreeninForm
      .get('secondaryScreening')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((isChecked) => {
        if (isChecked) {
          this.getFormControl('secondaryScreeningMethod')?.enable();
        } else {
          this.getFormControl('secondaryScreeningMethod')?.disable();
        }
      });
  }

  /**
   * set form value
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  setFormValue() {
    if (this.data) {
      this.newSecurityScreeninForm.patchValue(this.data);
    }
  }

  /**
   * get form control on specified field
   *
   * @param {string} field
   * @return {*}
   * @memberof NewSecurityScreeningModalComponent
   */
  getFormControl(field: string) {
    return this.newSecurityScreeninForm.get(field);
  }

  /**
   * clear form value on screen type change
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  clearFormValue() {
    const fieldListAndValue = [
      { key: 'screenerID', value: '' },
      { key: 'trackingNumber', value: '' },
      { key: 'secondaryScreening', value: false },
      { key: 'secondaryScreeningMethod', value: null },
      { key: 'sortDate', value: new Date() },
      { key: 'sortCode', value: null },
    ];
    fieldListAndValue.forEach((item) => {
      this.getFormControl(item.key)?.setValue(item.value);
    });
  }

  /**
   * show warning dialog when user cancel or close modal
   *
   * @param {boolean} [isCancelModal=false]
   * @memberof NewSecurityScreeningModalComponent
   */
  showWarningDialog(isCancelModal = false) {
    this.dialogRef.close();
    this.commonUtils
      .openWarningModal({
        header: isCancelModal ? CONST.CANCEL_MODAL : CONST.CLOSE_MODAL,
        body: CONST.BODY,
        confirmButtonText: CONST.CONFIRM_BUTTON,
        rejectButtonText: CONST.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.REJECT) {
          this.reOpenNewSecurityScreenModal();
        }
      });
  }

  /**
   * re-Open NewSecurityScreen Modal when reject button is clicked
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  reOpenNewSecurityScreenModal() {
    this.dialog
      .open(NewSecurityScreeningModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: this.newSecurityScreeninForm.value,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp && resp?.newSecuirtyScreen) {
          this.dataUtils.newSecurityScreen$.next(resp);
        }
      });
  }

  /**
   * submit form value when submit button is click and add record to table
   *
   * @memberof NewSecurityScreeningModalComponent
   */
  submitForm() {
    const formValue = this.newSecurityScreeninForm.value;
    this.dialogRef.close({ newSecuirtyScreen: formValue });
  }
}
